import React from 'react';
import { Palette, Square, MousePointer, Zap, Layers, CreditCard, Move, Hash, AlignVerticalSpaceAround } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';

const BlockStyleControls = ({ styles = {}, onChange, content = {}, onContentChange }) => {
  const updateStyle = (key, value) => {
    onChange({ ...styles, [key]: value });
  };

  // Apply card preset styling
  const applyCardStyle = (enabled) => {
    if (enabled) {
      onChange({
        ...styles,
        showAsCard: true,
        backgroundColor: styles.backgroundColor || '#ffffff',
        shadow: styles.shadow || 'md',
        borderRadius: styles.borderRadius || '12px',
        padding: styles.padding || '24px',
        borderColor: styles.borderColor || '#e2e8f0',
        borderWidth: styles.borderWidth || '1px',
      });
    } else {
      onChange({
        ...styles,
        showAsCard: false,
      });
    }
  };

  const colorPresets = [
    { name: 'None', value: 'transparent' },
    { name: 'White', value: '#ffffff' },
    { name: 'Light Gray', value: '#f8fafc' },
    { name: 'Light Blue', value: '#dbeafe' },
    { name: 'Light Green', value: '#dcfce7' },
    { name: 'Light Yellow', value: '#fef3c7' },
    { name: 'Light Pink', value: '#fce7f3' },
    { name: 'Light Purple', value: '#f3e8ff' },
  ];

  const borderColors = [
    { name: 'None', value: 'transparent' },
    { name: 'Gray', value: '#e2e8f0' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Indigo', value: '#6366f1' },
  ];

  const hoverEffects = [
    { name: 'None', value: 'none' },
    { name: 'Lift', value: 'lift' },
    { name: 'Shadow', value: 'shadow' },
    { name: 'Glow', value: 'glow' },
    { name: 'Darken', value: 'darken' },
    { name: 'Scale', value: 'scale' },
    { name: 'Border Highlight', value: 'border-highlight' },
  ];

  const clickEffects = [
    { name: 'None', value: 'none' },
    { name: 'Scale Down', value: 'scale-down' },
    { name: 'Ripple', value: 'ripple' },
    { name: 'Flash', value: 'flash' },
    { name: 'Shake', value: 'shake' },
  ];

  const shadowPresets = [
    { name: 'None', value: 'none' },
    { name: 'Subtle', value: 'sm' },
    { name: 'Medium', value: 'md' },
    { name: 'Large', value: 'lg' },
    { name: 'Extra Large', value: 'xl' },
    { name: 'Inner', value: 'inner' },
  ];

  const borderRadiusPresets = [
    { name: 'None', value: '0px' },
    { name: 'Subtle', value: '4px' },
    { name: 'Rounded', value: '8px' },
    { name: 'More Rounded', value: '16px' },
    { name: 'Pill', value: '9999px' },
  ];

  return (
    <div className="grid grid-cols-4 gap-2">
      {/* Spacing (margin above & below) */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              (styles.spaceAbove && styles.spaceAbove !== 'auto') || (styles.spaceBelow && styles.spaceBelow !== 'auto')
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-spacing-style"
            title="Adjust the gap above and below this block. Set to None to butt against the next block (no white gap)."
          >
            <AlignVerticalSpaceAround size={18} />
            <span>Spacing</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Spacing</Label>
              <p className="text-[11px] text-slate-500">Controls the gap between this block and the surrounding ones. Set both to "None" to butt this block against its neighbour (no white gap).</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Space Above</Label>
              <Select
                value={styles.spaceAbove || 'auto'}
                onValueChange={(value) => updateStyle('spaceAbove', value)}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="space-above-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (default)</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Space Below</Label>
              <Select
                value={styles.spaceBelow || 'auto'}
                onValueChange={(value) => updateStyle('spaceBelow', value)}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="space-below-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (default)</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Show as Card Toggle */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.showAsCard 
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700' 
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-card-style"
          >
            <CreditCard size={18} />
            <span>Card</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Show as Card</Label>
              <Switch
                checked={styles.showAsCard || false}
                onCheckedChange={applyCardStyle}
              />
            </div>
            {styles.showAsCard && (
              <>
                <div className="border-t pt-3">
                  <Label className="text-xs text-slate-500">Card Presets</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <button
                      onClick={() => onChange({
                        ...styles,
                        showAsCard: true,
                        backgroundColor: '#ffffff',
                        shadow: 'md',
                        borderRadius: '12px',
                        borderColor: '#e2e8f0',
                      })}
                      className="p-2 rounded border border-slate-200 hover:border-indigo-300 text-xs"
                    >
                      Clean
                    </button>
                    <button
                      onClick={() => onChange({
                        ...styles,
                        showAsCard: true,
                        backgroundColor: '#f8fafc',
                        shadow: 'lg',
                        borderRadius: '16px',
                        borderColor: 'transparent',
                      })}
                      className="p-2 rounded border border-slate-200 hover:border-indigo-300 text-xs"
                    >
                      Elevated
                    </button>
                    <button
                      onClick={() => onChange({
                        ...styles,
                        showAsCard: true,
                        backgroundColor: '#ffffff',
                        shadow: 'none',
                        borderRadius: '8px',
                        borderColor: '#6366f1',
                        borderWidth: '2px',
                      })}
                      className="p-2 rounded border border-slate-200 hover:border-indigo-300 text-xs"
                    >
                      Bordered
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Card Background</Label>
                  <Input
                    type="color"
                    value={styles.backgroundColor || '#ffffff'}
                    onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                    className="h-8 w-full mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Card Padding</Label>
                  <Select
                    value={styles.padding || '24px'}
                    onValueChange={(v) => updateStyle('padding', v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12px">Compact</SelectItem>
                      <SelectItem value="16px">Small</SelectItem>
                      <SelectItem value="24px">Medium</SelectItem>
                      <SelectItem value="32px">Large</SelectItem>
                      <SelectItem value="48px">Extra Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Background Color */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.backgroundColor && styles.backgroundColor !== 'transparent'
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-bg-color"
          >
            <Palette size={18} />
            <span>BG</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Preset Colors</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {colorPresets.map(color => (
                  <button
                    key={color.value}
                    onClick={() => updateStyle('backgroundColor', color.value)}
                    className={`w-8 h-8 rounded border-2 ${
                      styles.backgroundColor === color.value ? 'border-indigo-500' : 'border-slate-200'
                    }`}
                    style={{ backgroundColor: color.value === 'transparent' ? '#fff' : color.value }}
                    title={color.name}
                  >
                    {color.value === 'transparent' && <span className="text-xs text-slate-400">∅</span>}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Custom Color</Label>
              <Input
                type="color"
                value={styles.backgroundColor || '#ffffff'}
                onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                className="h-8 w-full mt-1"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Shape & Border */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.borderRadius && styles.borderRadius !== '0px'
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-shape"
          >
            <Square size={18} />
            <span>Shape</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Border Radius</Label>
              <div className="grid grid-cols-5 gap-1 mt-2">
                {borderRadiusPresets.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => updateStyle('borderRadius', preset.value)}
                    className={`p-2 rounded border text-xs ${
                      styles.borderRadius === preset.value 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    title={preset.name}
                  >
                    <div 
                      className="w-6 h-6 mx-auto bg-slate-300" 
                      style={{ borderRadius: preset.value }}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Border Color</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {borderColors.map(color => (
                  <button
                    key={color.value}
                    onClick={() => updateStyle('borderColor', color.value)}
                    className={`w-8 h-8 rounded border-2 ${
                      styles.borderColor === color.value ? 'border-indigo-500' : 'border-slate-200'
                    }`}
                    style={{ backgroundColor: color.value === 'transparent' ? '#fff' : color.value }}
                    title={color.name}
                  >
                    {color.value === 'transparent' && <span className="text-xs text-slate-400">∅</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Border Width</Label>
                <Select
                  value={styles.borderWidth || '0px'}
                  onValueChange={(value) => updateStyle('borderWidth', value)}
                >
                  <SelectTrigger className="h-7 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0px">None</SelectItem>
                    <SelectItem value="1px">1px</SelectItem>
                    <SelectItem value="2px">2px</SelectItem>
                    <SelectItem value="4px">4px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Padding</Label>
                <Select
                  value={styles.padding || '16px'}
                  onValueChange={(value) => updateStyle('padding', value)}
                >
                  <SelectTrigger className="h-7 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0px">None</SelectItem>
                    <SelectItem value="8px">S</SelectItem>
                    <SelectItem value="16px">M</SelectItem>
                    <SelectItem value="24px">L</SelectItem>
                    <SelectItem value="32px">XL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Shadow</Label>
              <div className="grid grid-cols-3 gap-1 mt-2">
                {shadowPresets.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => updateStyle('shadow', preset.value)}
                    className={`py-1.5 px-2 rounded border text-xs ${
                      styles.shadow === preset.value 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Animation removed - use the Animation controls at the bottom of each block instead */}

      {/* Hover Effects */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.hoverEffect && styles.hoverEffect !== 'none'
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-hover"
          >
            <MousePointer size={18} />
            <span>Hover</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Hover Effect</Label>
              <Select
                value={styles.hoverEffect || 'none'}
                onValueChange={(value) => updateStyle('hoverEffect', value)}
              >
                <SelectTrigger className="h-7 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hoverEffects.map(effect => (
                    <SelectItem key={effect.value} value={effect.value}>
                      {effect.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {styles.hoverEffect === 'glow' && (
              <div>
                <Label className="text-xs">Glow Color</Label>
                <Input
                  type="color"
                  value={styles.glowColor || '#6366f1'}
                  onChange={(e) => updateStyle('glowColor', e.target.value)}
                  className="h-8 w-full mt-1"
                />
              </div>
            )}
            {styles.hoverEffect === 'border-highlight' && (
              <div>
                <Label className="text-xs">Highlight Color</Label>
                <Input
                  type="color"
                  value={styles.highlightColor || '#6366f1'}
                  onChange={(e) => updateStyle('highlightColor', e.target.value)}
                  className="h-8 w-full mt-1"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Transition Speed</Label>
              <Select
                value={styles.transitionDuration || '0.2s'}
                onValueChange={(value) => updateStyle('transitionDuration', value)}
              >
                <SelectTrigger className="h-7 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.1s">Fast</SelectItem>
                  <SelectItem value="0.2s">Normal</SelectItem>
                  <SelectItem value="0.3s">Slow</SelectItem>
                  <SelectItem value="0.5s">Very Slow</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Click Effects */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.clickEffect && styles.clickEffect !== 'none'
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-click"
          >
            <Zap size={18} />
            <span>Click</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Click Effect</Label>
              <Select
                value={styles.clickEffect || 'none'}
                onValueChange={(value) => updateStyle('clickEffect', value)}
              >
                <SelectTrigger className="h-7 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {clickEffects.map(effect => (
                    <SelectItem key={effect.value} value={effect.value}>
                      {effect.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Image Size - only show for image-related blocks */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white"
            data-testid="block-image-size"
          >
            <Layers size={18} />
            <span>Image</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Size By</Label>
              <Select
                value={styles.imageSizeMode || 'width'}
                onValueChange={(value) => updateStyle('imageSizeMode', value)}
              >
                <SelectTrigger className="h-7 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="width">Width (auto height)</SelectItem>
                  <SelectItem value="height">Height (auto width)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(styles.imageSizeMode || 'width') === 'width' ? (
              <div>
                <Label className="text-xs">Width</Label>
                <Select
                  value={styles.imageWidth || '100%'}
                  onValueChange={(value) => updateStyle('imageWidth', value)}
                >
                  <SelectTrigger className="h-7 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25%">25%</SelectItem>
                    <SelectItem value="50%">50%</SelectItem>
                    <SelectItem value="75%">75%</SelectItem>
                    <SelectItem value="100%">100%</SelectItem>
                    <SelectItem value="200px">200px</SelectItem>
                    <SelectItem value="300px">300px</SelectItem>
                    <SelectItem value="500px">500px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs">Height</Label>
                <Select
                  value={styles.imageHeight || '300px'}
                  onValueChange={(value) => updateStyle('imageHeight', value)}
                >
                  <SelectTrigger className="h-7 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100px">100px</SelectItem>
                    <SelectItem value="150px">150px</SelectItem>
                    <SelectItem value="200px">200px</SelectItem>
                    <SelectItem value="300px">300px</SelectItem>
                    <SelectItem value="400px">400px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs">Alignment</Label>
              <Select
                value={styles.imageAlign || 'center'}
                onValueChange={(value) => updateStyle('imageAlign', value)}
              >
                <SelectTrigger className="h-7 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Container Width Control */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
              styles.containerWidth && styles.containerWidth !== '100%'
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
            }`}
            data-testid="block-width"
          >
            <Move size={18} />
            <span>Width</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-4">
            {/* Explanatory note. Users often expect "Width" to visibly
                resize the paragraph or heading, but on text-only blocks
                inside an already-narrow reading column (Articles,
                sidebar-heavy pages), shrinking the block just adds equal
                margins on both sides — it *looks* like indenting. This
                note surfaces that the width controls the block's box,
                not the typography, and is most visible when the block
                also has a background colour or border. */}
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-900">
              <strong>What Width does:</strong> resizes the block's <em>box</em> (background, border area) within its column — not the text itself. On plain-text blocks the change may just look like indentation. Add a Background Color or Border to see it clearly.
            </div>
            <div>
              <Label className="text-xs font-medium">Container Width</Label>
              <p className="text-[10px] text-slate-500 mb-2">Adjust the width of this block</p>
              <div className="grid grid-cols-4 gap-1 mb-3">
                {[
                  { label: '25%', value: '25%' },
                  { label: '50%', value: '50%' },
                  { label: '75%', value: '75%' },
                  { label: '100%', value: '100%' },
                ].map((w) => (
                  <button
                    key={w.value}
                    onClick={() => updateStyle('containerWidth', w.value)}
                    className={`py-1.5 px-2 rounded border text-xs font-medium transition-all ${
                      (styles.containerWidth || '100%') === w.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              
              {/* Custom width input */}
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-xs text-slate-500">Custom:</Label>
                <Input
                  type="number"
                  min="100"
                  max="1200"
                  value={parseInt(styles.containerWidthPx) || 800}
                  onChange={(e) => updateStyle('containerWidthPx', `${e.target.value}px`)}
                  className="h-7 w-20 text-xs"
                />
                <span className="text-xs text-slate-500">px</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    updateStyle('containerWidth', 'custom');
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs font-medium">Max Width Presets</Label>
              <div className="grid grid-cols-3 gap-1 mt-2">
                {[
                  { label: 'Small', value: '640px' },
                  { label: 'Medium', value: '768px' },
                  { label: 'Large', value: '1024px' },
                  { label: 'XL', value: '1280px' },
                  // "Full" = full-bleed edge-to-edge. Renderer detects the
                  // sentinel `100vw` and emits the breakout margins
                  // `calc(-50vw + 50%)` so the block escapes the page's
                  // max-w-* container and the background reaches both
                  // viewport edges. The original `100%` value only sized
                  // the block to its parent (indistinguishable from
                  // "None"), which was confusing.
                  { label: 'Full', value: '100vw' },
                  { label: 'None', value: 'none' },
                ].map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => updateStyle('maxWidth', preset.value)}
                    className={`py-1.5 px-2 rounded border text-xs ${
                      styles.maxWidth === preset.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs font-medium">Horizontal Alignment</Label>
              <div className="grid grid-cols-3 gap-1 mt-2">
                {[
                  { label: 'Left', value: 'flex-start' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'flex-end' },
                ].map((align) => (
                  <button
                    key={align.value}
                    onClick={() => updateStyle('containerAlign', align.value)}
                    className={`py-1.5 px-2 rounded border text-xs ${
                      (styles.containerAlign || 'center') === align.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {align.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Anchor ID for linking */}
      {onContentChange && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`flex flex-col items-center justify-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all ${
                content.anchor_id
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-white'
              }`}
              data-testid="block-anchor-id"
            >
              <Hash size={18} />
              <span>Anchor</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3">
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Anchor ID</Label>
                <p className="text-xs text-slate-400 mt-1">
                  Create a link target so other buttons can jump to this section
                </p>
              </div>
              <Input
                value={content.anchor_id || ''}
                onChange={(e) => {
                  // Sanitize: lowercase, replace spaces with dashes, remove special chars
                  const sanitized = e.target.value
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '');
                  onContentChange({ ...content, anchor_id: sanitized });
                }}
                placeholder="e.g., contact-form, about-section"
                className="h-8"
              />
              {content.anchor_id && (
                <div className="text-xs bg-slate-800/50 text-slate-300 px-2 py-1.5 rounded">
                  Link to this block: <code className="text-indigo-400">#{content.anchor_id}</code>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default BlockStyleControls;
